import { tKey, type I18nKey } from "@/core/i18n";
import { PreAnnotateModelTypeEnum } from "@repo/schema";

/**
 * Every user-facing string of the auto-annotate settings dialog, in one place,
 * as `label` catalog keys rendered with `t()`.
 *
 * The copy was written against the "Robopipe studio" design and reviewed as
 * prose; keeping it out of the JSX means a wording change is a one-line diff
 * that cannot accidentally touch layout, and the same phrases can be reused by
 * the label page (Save/✨ disabled reasons) without drifting. Counted phrases
 * (the run outcome's "Added 3 boxes", the notes warning) are plural keys and
 * are written out where they render, so i18next-cli sees their `count`.
 */

type ModeKeys = Record<PreAnnotateModelTypeEnum, I18nKey<"label">>;

export const DIALOG_TITLE = tKey("label:actions.autoAnnotateSettings");

// ─── Run outcome banner ───────────────────────────────────────────────────────

export const BANNER_DONE_BODY = tKey("label:runOutcome.doneBody");
// The model looked and reported nothing. The descriptions are what it looked
// for, so that is where the fix is.
export const BANNER_EMPTY_TITLE = tKey("label:runOutcome.emptyTitle");
export const BANNER_EMPTY_BODY = tKey("label:runOutcome.emptyBody");
export const BANNER_ERROR_TITLE = tKey("label:runOutcome.errorTitle");
export const BANNER_ERROR_BODY = tKey("label:runOutcome.errorBody");

// ─── Default mode ─────────────────────────────────────────────────────────────

export const DEFAULT_MODE_TITLE = tKey("label:settings.defaultMode.title");
export const DEFAULT_MODE_HELP = tKey("label:settings.defaultMode.help");
export const MODE_LABEL = {
  [PreAnnotateModelTypeEnum.SEGMENTATION]: tKey("label:mode.segmentation"),
  [PreAnnotateModelTypeEnum.DETECTION]: tKey("label:mode.detection"),
} satisfies ModeKeys;

// ─── Label descriptions ───────────────────────────────────────────────────────

export const DESCRIPTIONS_TITLE = tKey("label:settings.descriptions.title");
export const DESCRIPTIONS_HELP = tKey("label:settings.descriptions.help");
export const DESCRIPTIONS_FOOTER = tKey("label:settings.descriptions.footer");
export const DESCRIPTIONS_NO_LABELS = tKey("label:settings.descriptions.noLabels");
export const DESCRIPTION_GENERATE_FAILED = tKey("label:settings.descriptions.generateFailed");
/** `{{name}}`: the label's name, lower-cased. */
export const DESCRIPTION_PLACEHOLDER = tKey("label:settings.descriptions.placeholder");
/** `{{name}}`: the label's name. */
export const DESCRIPTION_ARIA_LABEL = tKey("label:settings.descriptions.ariaLabel");
export const DESCRIPTIONS_BUSY = tKey("label:settings.descriptions.busy");

// ─── Project notes ────────────────────────────────────────────────────────────

export const NOTES_TITLE = tKey("label:settings.notes.title");
export const NOTES_HELP = tKey("label:settings.notes.help");
export const NOTES_FOOTER = tKey("label:settings.notes.footer");
export const NOTES_EMPTY = tKey("label:settings.notes.empty");
export const NOTES_LOAD_FAILED = tKey("label:settings.notes.loadFailed");
export const NOTES_REMOVE = tKey("label:actions.remove");
export const NOTES_EDIT = tKey("common:actions.edit");
export const NOTES_ADD = tKey("label:settings.notes.add");
export const NOTES_SAVE = tKey("label:actions.saveNote");
export const NOTES_CANCEL = tKey("common:actions.cancel");
export const NOTES_EDITED = tKey("label:settings.notes.edited");
export const NOTES_TEXT_LABEL = tKey("label:settings.notes.textLabel");
export const NOTES_ADD_PLACEHOLDER = tKey("label:settings.notes.addPlaceholder");
export const NOTES_SAVE_FAILED = tKey("label:settings.notes.saveFailed");
export const NOTES_OPEN_IMAGE = tKey("label:settings.notes.openImage");
export const NOTES_FORMER_MEMBER = tKey("label:settings.notes.formerMember");
// Past VISION_NOTES_WARN_COUNT / VISION_NOTES_WARN_CHARS, "N notes are sent
// with every run…" (`settings.notes.tooMany`, plural) — a nudge, never a
// block: every note is still sent.

// ─── Availability / save gate ─────────────────────────────────────────────────

export const VISION_UNAVAILABLE = tKey("label:settings.visionUnavailable.title");
/** What an admin has to change; the title alone does not say where to look. */
export const VISION_UNAVAILABLE_DETAIL = tKey("label:settings.visionUnavailable.detail");
/** Vision detects boxes only for now — the toggle and the ✨ menu both say so. */
export const VISION_SEGMENTATION_SOON = tKey("label:settings.visionSegmentationSoon");
export const SAVE_BLOCKED_DESCRIBE = tKey("label:settings.saveBlocked.describe");
export const SAVE_FAILED = tKey("label:settings.saveFailed");

// ─── Footer ───────────────────────────────────────────────────────────────────

export const FOOTER_CANCEL = tKey("common:actions.cancel");
export const FOOTER_SAVE = tKey("common:actions.save");
export const FOOTER_SAVING = tKey("common:actions.saving");
